import { CheckerComments, PointStatus, ValidationLevel } from '@luzula/luzula-lib';

/**
 * Reine Auswertungs-Logik für das Kontroll-Auswertungs-Modal (pro Projekt).
 *
 * Bewusst frei von Angular/DB: nimmt eine bereits gefilterte Punktliste entgegen
 * und liefert das fertige Statistik-Objekt. So ist die Zählerei isoliert testbar;
 * der `DataService` baut nur die `ControlEvalPoint[]` aus dem Index zusammen.
 *
 * Wichtig: zwei klar getrennte Welten —
 *  - **Automatische Validierungen** (`dataSeverity` je Punkt, vom System geprüft),
 *  - **Kommentare aus der Kontrolle** (`checkerComments` je Feld, von Prüfern).
 */

/** Fixe Anzeige-Reihenfolge der Punktstati (entspricht dem Workflow-Durchlauf). */
export const CONTROL_STATUS_ORDER: PointStatus[] = [
	'anstehend', 'bearbeitung', 'kontrolle', 'in_pruefung', 'abgeschlossen', 'abgelehnt',
];

/** Ein Punkt, reduziert auf das für die Auswertung Nötige. */
export interface ControlEvalPoint {
	id: string;
	nummer?: string;
	/** Bestandsdaten ohne Workflow werden vom Aufrufer auf 'abgeschlossen' gemappt. */
	status: PointStatus;
	/** Höchste Schwere der AUTOMATISCHEN Validierung dieses Punkts (state.dataSeverity). */
	dataSeverity?: ValidationLevel;
	/**
	 * Workflow-Übergänge (workflow.history). Für die Personen-Statistik: `to`
	 * bestimmt die Tätigkeit (kontrolle = vorgelegt, abgeschlossen = abgeschlossen),
	 * `user` ist der Klarname der handelnden Person.
	 */
	history?: { to?: string; user?: string }[] | null;
	/**
	 * Bearbeiter-Verlauf (workflow.editors) — jeder, der den Punkt in Bearbeitung
	 * genommen hat (angelegt/gegriffen). Für die „bearbeitet"-Spalte der Kartierer.
	 */
	editors?: { user?: string }[] | null;
	/**
	 * Feldname → Kommentar-Thread. Akzeptiert sowohl die laufzeit-`Map` als auch
	 * das DB-`Record` (JSON) — wird intern normalisiert.
	 */
	comments?: Map<string, CheckerComments> | Record<string, CheckerComments> | null;
}

/** Zähler für eine Kommentar-Schwere-Stufe (rot/gelb/blau). */
export interface SeverityStat {
	open: number;
	resolved: number;
	total: number;
}

export interface StatusSlice {
	status: PointStatus;
	count: number;
}

/**
 * Automatische Validierungen — Punktzähler nach höchster Schwere je Punkt.
 * Anders als Kommentare gibt es hier kein „offen/erledigt": die Prüfung wird
 * jedes Mal neu berechnet, der Wert spiegelt den aktuellen Stand.
 */
export interface AutoValidationStat {
	/** Punkte mit automatischem Fehler (danger). */
	danger: number;
	/** Punkte mit automatischer Warnung (warning, ohne Fehler). */
	warning: number;
	/** Punkte mit irgendeinem automatischen Befund (danger + warning). */
	pointsWithIssues: number;
}

export interface FieldHotspot {
	/** Roher Feldschlüssel (z.B. "Vegetation[0]"); Aufrufer humanisiert per displayString. */
	field: string;
	/** Anzahl Kommentar-Threads zu diesem Feld über alle Punkte. */
	count: number;
}

/** Leistung einer Person (z.B. Kontrolleur) — Anzahl Punkte. */
export interface PersonStat {
	/** Roher Nutzer (für Drill-Matching gegen history/editors). */
	name: string;
	/** Anzeige-Name (synthetische Nutzer umbenannt, sonst = name). */
	label: string;
	count: number;
}

/** Kartierer-Leistung je Person: bearbeitet (in Bearbeitung gehabt) + vorgelegt. */
export interface KartiererStat {
	/** Roher Nutzer (für Drill-Matching gegen editors/history). */
	name: string;
	/** Anzeige-Name (synthetische Nutzer umbenannt, sonst = name). */
	label: string;
	/** Punkte, die die Person in Bearbeitung hatte (angelegt/gegriffen, editors). */
	bearbeitet: number;
	/** Punkte, die die Person zur Kontrolle vorgelegt hat (→ kontrolle). */
	vorgelegt: number;
}

export interface ControlEvaluation {
	totalPoints: number;
	statusSlices: StatusSlice[];
	completedCount: number;
	completedPct: number;

	/** Automatische Validierungen (Systemprüfung), klar getrennt von Kommentaren. */
	autoValidation: AutoValidationStat;

	/** Kommentare aus der Kontrolle (von Prüfern), je Feld. */
	danger: SeverityStat;
	warning: SeverityStat;
	ok: SeverityStat;

	/** Summe aller Feld-Kommentar-Threads (= danger + warning + ok). */
	totalComments: number;
	/** Threads mit ≥2 Nachrichten (jemand hat geantwortet). */
	discussionsTotal: number;
	discussionsOpen: number;

	pointsWithComments: number;
	pointsClean: number;

	/** Häufigste beanstandete Felder (max. 5). */
	topFields: FieldHotspot[];

	/** Kartierer-Leistung je Person: bearbeitet (editors) + vorgelegt (→ kontrolle). */
	kartierer: KartiererStat[];
	/** Kontrolleur-Leistung: abgeschlossene Punkte je Person (→ abgeschlossen), meiste zuerst. */
	kontrolleure: PersonStat[];
}

/** Anzahl der häufigsten Felder, die im Modal gelistet werden. */
const TOP_FIELDS_LIMIT = 5;

/** Technische/synthetische Nutzer (z.B. Migration) → menschenlesbares Anzeige-Label. */
const SYNTHETIC_USER_LABELS: Record<string, string> = {
	system: 'System (Migration)',
};

/**
 * Baut die `ControlEvalPoint[]` aus den `queryIndex(true)`-Rows für ein Projekt.
 *
 * Filtert nach Projekt-Zugehörigkeit (`key[4].projects`) und liest Status
 * (`key[9].status`, fehlt → 'abgeschlossen'), Nummer (`key[4].nummer`), die
 * automatische Schwere (`key[5].dataSeverity`) sowie die `checkerComments` aus
 * dem mitgelieferten Voll-Dokument (`doc`). Isoliert von Angular/DB, damit die
 * fragile Index-Key-Zuordnung direkt testbar bleibt.
 */
export function controlEvalPointsFromIndexRows(
	rows: any[] | undefined,
	projectId: string,
): ControlEvalPoint[] {
	const points: ControlEvalPoint[] = [];
	if (!rows || !projectId) return points;
	for (const row of rows) {
		const projects = row?.key?.[4]?.projects;
		if (!Array.isArray(projects)) continue;
		if (!projects.some((p: any) => p?.id === projectId)) continue;
		points.push({
			id: row.id,
			nummer: row.key?.[4]?.nummer,
			status: row.key?.[9]?.status ?? 'abgeschlossen',
			dataSeverity: row.key?.[5]?.dataSeverity,
			history: row.doc?.workflow?.history ?? null,
			editors: row.doc?.workflow?.editors ?? null,
			comments: row.doc?.checkerComments ?? null,
		});
	}
	return points;
}

function normalizeComments(
	c: ControlEvalPoint['comments'],
): Record<string, CheckerComments> {
	if (!c) return {};
	if (c instanceof Map) return Object.fromEntries(c.entries());
	if (typeof c === 'object') return c as Record<string, CheckerComments>;
	return {};
}

function emptySeverity(): SeverityStat {
	return { open: 0, resolved: 0, total: 0 };
}

export function computeControlEvaluation(points: ControlEvalPoint[]): ControlEvaluation {
	const statusMap = new Map<PointStatus, number>();
	const autoValidation: AutoValidationStat = { danger: 0, warning: 0, pointsWithIssues: 0 };
	const danger = emptySeverity();
	const warning = emptySeverity();
	const ok = emptySeverity();
	let discussionsTotal = 0;
	let discussionsOpen = 0;
	let pointsWithComments = 0;
	const fieldCounts = new Map<string, number>();
	// Personen-Leistung: pro Person die DISTINKTEN Punkte (Set) je Tätigkeit — so
	// zählt ein mehrfach vorgelegter Punkt nur einmal.
	const bearbeitetMap = new Map<string, Set<string>>();
	const vorgelegtMap = new Map<string, Set<string>>();
	const abgeschlossenMap = new Map<string, Set<string>>();
	const credit = (m: Map<string, Set<string>>, user: string, pointId: string) => {
		let set = m.get(user);
		if (!set) m.set(user, set = new Set<string>());
		set.add(pointId);
	};

	for (const p of points) {
		statusMap.set(p.status, (statusMap.get(p.status) ?? 0) + 1);

		// Wer hatte den Punkt in Bearbeitung (angelegt/gegriffen)?
		for (const e of p.editors ?? []) {
			if (e?.user) credit(bearbeitetMap, e.user, p.id);
		}
		// Wer hat vorgelegt / abgeschlossen (Klarname in user)?
		for (const h of p.history ?? []) {
			if (!h?.user) continue;
			if (h.to === 'kontrolle') credit(vorgelegtMap, h.user, p.id);
			else if (h.to === 'abgeschlossen') credit(abgeschlossenMap, h.user, p.id);
		}

		// Automatische Validierung (je Punkt, höchste Schwere).
		if (p.dataSeverity === ValidationLevel.danger) {
			autoValidation.danger++;
			autoValidation.pointsWithIssues++;
		} else if (p.dataSeverity === ValidationLevel.warning) {
			autoValidation.warning++;
			autoValidation.pointsWithIssues++;
		}

		// Kommentare aus der Kontrolle (je Feld).
		const threads = Object.entries(normalizeComments(p.comments));
		if (threads.length > 0) pointsWithComments++;

		for (const [field, thread] of threads) {
			if (!thread) continue;
			const bucket =
				thread.type === ValidationLevel.danger ? danger
				: thread.type === ValidationLevel.warning ? warning
				: ok;
			bucket.total++;
			if (thread.resolved) bucket.resolved++; else bucket.open++;

			const msgCount = Array.isArray(thread.comments) ? thread.comments.length : 0;
			if (msgCount > 1) {
				discussionsTotal++;
				if (!thread.resolved) discussionsOpen++;
			}

			fieldCounts.set(field, (fieldCounts.get(field) ?? 0) + 1);
		}
	}

	const totalPoints = points.length;
	const completedCount = statusMap.get('abgeschlossen') ?? 0;
	const completedPct = totalPoints ? Math.round((completedCount / totalPoints) * 100) : 0;

	const statusSlices = CONTROL_STATUS_ORDER.map(status => ({
		status,
		count: statusMap.get(status) ?? 0,
	}));

	const topFields = [...fieldCounts.entries()]
		.map(([field, count]) => ({ field, count }))
		.sort((a, b) => b.count - a.count || a.field.localeCompare(b.field, 'de'))
		.slice(0, TOP_FIELDS_LIMIT);

	// Synthetische/technische Nutzer (z.B. Migration) fürs Anzeigen umbenennen.
	const personLabel = (raw: string): string =>
		SYNTHETIC_USER_LABELS[(raw ?? '').toLowerCase()] ?? raw;

	// name = roher Nutzer (für Drill-Matching gegen history/editors), label = Anzeige.
	const toPersonStats = (m: Map<string, Set<string>>): PersonStat[] =>
		[...m.entries()]
			.map(([name, set]) => ({ name, label: personLabel(name), count: set.size }))
			.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'de'));

	// Kartierer: bearbeitet- und vorgelegt-Maps zu EINER Liste je Person mergen.
	const kartiererNames = new Set<string>([...bearbeitetMap.keys(), ...vorgelegtMap.keys()]);
	const kartierer: KartiererStat[] = [...kartiererNames]
		.map(name => ({
			name,
			label: personLabel(name),
			bearbeitet: bearbeitetMap.get(name)?.size ?? 0,
			vorgelegt: vorgelegtMap.get(name)?.size ?? 0,
		}))
		.sort((a, b) =>
			(b.bearbeitet + b.vorgelegt) - (a.bearbeitet + a.vorgelegt)
			|| a.label.localeCompare(b.label, 'de'));

	return {
		totalPoints,
		statusSlices,
		completedCount,
		completedPct,
		autoValidation,
		danger,
		warning,
		ok,
		totalComments: danger.total + warning.total + ok.total,
		discussionsTotal,
		discussionsOpen,
		pointsWithComments,
		pointsClean: totalPoints - pointsWithComments,
		topFields,
		kartierer,
		kontrolleure: toPersonStats(abgeschlossenMap),
	};
}

/** Ergebnis von `getControlEvaluationForProject`: Statistik + die Rohpunkte (für Drill-down). */
export interface ControlEvaluationResult {
	evaluation: ControlEvaluation;
	points: ControlEvalPoint[];
}

/**
 * Drill-down-Kriterium: beschreibt, welche Punkte ein Klick im Auswertungs-Modal
 * herausfiltern soll. Wird mit {@link pointIdsForDrill} auf die schon geladenen
 * Punkte angewandt — kein neues Filter-Feld nötig.
 */
export type ControlDrill =
	| { kind: 'status'; status: PointStatus }
	| { kind: 'auto'; severity: 'danger' | 'warning' }
	| { kind: 'comment'; severity: 'danger' | 'warning' | 'ok'; resolved: boolean }
	| { kind: 'field'; field: string }
	| { kind: 'person'; activity: 'bearbeitet' | 'vorgelegt' | 'abgeschlossen'; name: string };

const DRILL_SEVERITY: Record<'danger' | 'warning' | 'ok', ValidationLevel> = {
	danger: ValidationLevel.danger,
	warning: ValidationLevel.warning,
	ok: ValidationLevel.ok,
};

function matchesDrill(p: ControlEvalPoint, d: ControlDrill): boolean {
	switch (d.kind) {
		case 'status':
			return p.status === d.status;
		case 'auto':
			return p.dataSeverity === DRILL_SEVERITY[d.severity];
		case 'comment': {
			const target = DRILL_SEVERITY[d.severity];
			return Object.values(normalizeComments(p.comments))
				.some(t => !!t && t.type === target && !!t.resolved === d.resolved);
		}
		case 'field':
			return Object.prototype.hasOwnProperty.call(normalizeComments(p.comments), d.field);
		case 'person':
			if (d.activity === 'bearbeitet') return (p.editors ?? []).some(e => e?.user === d.name);
			return (p.history ?? []).some(
				h => h?.user === d.name && h?.to === (d.activity === 'vorgelegt' ? 'kontrolle' : 'abgeschlossen'),
			);
	}
}

/** IDs aller Punkte, die auf das Drill-Kriterium passen (für FilterService.applyAdHocView). */
export function pointIdsForDrill(points: ControlEvalPoint[], drill: ControlDrill): string[] {
	return points.filter(p => matchesDrill(p, drill)).map(p => p.id);
}
